import { defineStore } from 'pinia';
import { watch, ref, computed, nextTick } from 'vue';
import * as Sentry from '@sentry/vue';
import { useRoute, useRouter } from 'vue-router';
import { useGeoIPStore } from './geoip';
import { useAnalyticsStore } from './analytics';
import { checkPlan, verifyTokenUser, getUserSubscriptions, createPurchase, getCoupon } from '@/services/backend';

import { verifyToken } from '@/services/jose';
import countryCurrencyMap from '@/services/countryCurrencyMap';
import { updateUrlQuery, removeUrlQuery } from '@/services/windowAPI';
import { orderCompleteRedirect } from '@/services/orderCompleteURL';

const quantityLimit = Number(import.meta.env.VITE_QUANTITY_LIMIT);
const routerCouponPrefixes = import.meta.env.VITE_ROUTER_COUPON_PREFIXES;
const enableIndividualServers = import.meta.env.VITE_ENABLE_INDIVIDUAL_SERVERS === 'true';

export const useCheckoutStore = defineStore('checkout', () => {
  const geoIPStore = useGeoIPStore();
  const analyticsStore = useAnalyticsStore();
  const route = useRoute();
  const router = useRouter();
  const planDescription = ref({});

  const state = ref({ // refs become state properties in setup stores
    firstName: '',
    lastName: '',
    email: '',
    emailRepeat: '',
    plan: route.query.plan || 'speedify-unlimited-monthly',
    country: geoIPStore.details.country || 'US',
    postalCode: '',
    currency: geoIPStore.details.currency || 'USD',
    quantity: route.query.quantity || 1,
    serverCode: route.query.serverCode || '',
    serverName: '',
    servers: route.query.servers || 0,
    totalPrice: 0,
    upsold: false,
    vatNumber: '',
    loggedIn: null,
    isPaid: null,
    // planTrial is for free trials that are attached to the selected plan
    //  - covers cardless trials as well, but not all trials are cardless
    planTrial: false,
    // couponTrial tracks whether the coupon that's redeemed offers a trial
    //  - we turn this off if customer redeems a different type of coupon
    couponTrial: false,
    trialLength: '',
    coupon: {
      success: false,
      invalid: false,
      invalidMessage: 'Invalid coupon code',
      // used to v-model coupon input and fed to validatedCode when recurlyJS checks it
      inputtedCode: route.query.coupon || '',
      validatedCode: '',
      routerCode: '', // supplied when manually inputted coupon belongs to a router coupon batch
    },
    hasRouterSubs: null,
    company: '',
  });

  const paymentState = ref({
    processing: false,
    purchaseFailed: false,
    purchasePartiallySucceeded: false,
    failureError: '',
  });

  // Per-panel error state, keyed by payment method. Lives here (rather than in each
  // component) so it's centralized, but kept per-method so the simultaneously-mounted
  // panels don't show each other's errors.
  const panelState = ref({
    creditCard: { methodError: '', partialSuccess: false },
    paypal: { methodError: '', partialSuccess: false },
    venmo: { methodError: '', partialSuccess: false },
    applePay: { methodError: '', partialSuccess: false },
    googlePay: { methodError: '', partialSuccess: false },
  });

  const checkoutPricing = ref(null);

  const initializeCheckoutPricing = (cartState) => {
    checkoutPricing.value = recurly.Pricing.Checkout();
    checkoutPricing.value.on('error', err => {
      const expectedCodes = ['not-found', 'invalid-parameter'];
      const isExpected = expectedCodes.includes(err?.code);

      if (!isExpected) {
        console.error('Recurly pricing error:', err);
        Sentry.captureException(err, {
          tags: { component: 'CheckoutCart', operation: 'recurly_pricing_error' },
        });
      }
    });

    // Both the set.address and change handlers set the pricing currency, and each
    // .currency() call re-emits 'change', so an unguarded call from inside a handler
    // re-enters the other one (PLN↔EUR ping-pong for VAT countries whose country
    // currency isn't EUR). Skip codes Recurly is already on or already switching to;
    // the pending code is cleared once a 'change' reports it, so a stale reprice in
    // the old currency still re-triggers the switch.
    let pendingCurrency = null;
    const applyCurrency = (code, currentCode) => {
      if (code === currentCode || code === pendingCurrency) return;
      pendingCurrency = code;
      checkoutPricing.value.currency(code)
        .catch(err => {
          // rejection doesn't emit 'change', so clearing the guard can't loop
          pendingCurrency = null;
          console.error('Recurly currency pricing error:', err);
          Sentry.captureException(err, {
            tags: { component: 'CheckoutCart', operation: 'recurly_currency_error' },
          });
        })
        .done();
    };

    checkoutPricing.value.on('set.address', addressObj => {
      // VAT must be charged in EUR; once we know VAT applies, address edits must
      // not pull the currency back to the country default
      applyCurrency(
        cartState.vat.applicable ? 'EUR' : countryCurrencyMap(addressObj.country),
        checkoutPricing.value.items?.currency,
      );
    });

    checkoutPricing.value.on('change', pricingState => {
      cartState.plan.price = Number(pricingState.now?.items[0]?.amount) || 0;
      cartState.plan.name = checkoutPricing.value.items?.subscriptions[0]?.subscription?.items?.plan?.name;
      cartState.pricing.planNext = Number(pricingState.next?.items[0]?.amount) || 0;
      cartState.pricing.servers = Number(pricingState.now?.items[1]?.plan) || 0;
      cartState.pricing.taxes = Number(pricingState.now?.taxes) || 0;
      cartState.vat.applicable = Boolean(pricingState.taxes[0]?.type === 'vat');
      cartState.pricing.discount = Number(pricingState.now?.discount) || 0;
      cartState.pricing.plan = Number(pricingState.now?.items[0]?.plan) || 0;
      state.value.totalPrice = Number(pricingState.now?.total) || 0;
      // Recurly only taxes what's charged in each period, so during a trial
      // now.taxes is 0 and the tax lives in next.taxes alongside the next plan price
      cartState.pricing.chargedAfterTrial = false;
      cartState.pricing.totalAfterTrial = Number(pricingState.next?.total) || 0;
      if (subscriptionProductRequiresCoupon.value && !cartState.checkout.hideBillingForm) {
        cartState.pricing.plan = Number(pricingState.next?.items[0]?.plan);
        cartState.pricing.taxes = Number(pricingState.next?.taxes) || 0;
        state.value.totalPrice = Number(pricingState.next.total);
        state.value.planTrial = false;
      } else if (subscriptionProductRequiresCoupon.value && cartState.checkout.hideBillingForm) {
        cartState.pricing.plan = Number(pricingState.next?.items[0]?.plan);
        cartState.pricing.taxes = Number(pricingState.next?.taxes) || 0;
        cartState.pricing.chargedAfterTrial = true;
        state.value.totalPrice = Number(pricingState.now?.total) || 0;
      } else if (freeTrialActive.value) {
        cartState.pricing.plan = Number(pricingState.next?.items[0]?.plan);
        cartState.pricing.taxes = Number(pricingState.next?.taxes) || 0;
        cartState.pricing.chargedAfterTrial = true;
      }
      // the label must always match the amounts above; while a VAT switch to EUR is
      // in flight these are still native-currency amounts, so don't relabel them
      const pricingCurrency = pricingState.currency?.code;
      state.value.currency = pricingCurrency;
      if (pricingCurrency === pendingCurrency) {
        pendingCurrency = null;
      }
      if (cartState.vat.applicable) {
        applyCurrency('EUR', pricingCurrency);
      }
      if (!cartState.vat.applicable) {
        cartState.vat.exemptionApplied = false;
        // when VAT stops applying (e.g. country switched off a VAT country),
        // fall back to the country currency instead of staying stuck on EUR
        applyCurrency(countryCurrencyMap(state.value.country), pricingCurrency);
      }
    });

    checkoutPricing.value.on('set.plan', planObj => {
      cartState.plan.interval = planObj.plan.period.length === 12 ? 'yearly' : 'monthly';
      if (planObj.plan.trial) {
        state.value.planTrial = true;
        state.value.trialLength = `${planObj.plan.trial.length} ${planObj.plan.trial.interval}`;
      }
      if (planObj.subscription.id === '1') {
        state.value.serverName = planObj.plan.name;
      }
      cartState.formVisibility = 'visible';
    });

    checkoutPricing.value.on('set.coupon', coupon => {
      resetPaymentErrors();
      state.value.coupon.success = true;
      state.value.coupon.invalid = false;
      state.value.coupon.validatedCode = state.value.coupon.inputtedCode;
      updateUrlQuery('coupon', state.value.coupon.validatedCode);
      if (coupon.discount.type === 'free_trial') {
        const pluralChar = coupon.discount.trial.amount === 1 ? '' : 's';
        state.value.trialLength = `${coupon.discount.trial.amount} ${coupon.discount.trial.unit}${pluralChar}`;
        state.value.couponTrial = true;
      } else {
        state.value.couponTrial = false;
      }
      if (state.value.plan.includes('cardless')) {
        if (coupon.applies_to_plans
          && !coupon.applies_to_all_plans
          && coupon.discount.type === 'free_trial'
        ) {
          cartState.checkout.hideBillingForm = true;
          state.value.totalPrice = 0;
        } else {
          cartState.checkout.hideBillingForm = false;
        }
      }
    });

    checkoutPricing.value.on('error.coupon', async () => {
      const couponInfo = await getCoupon(state.value.coupon.inputtedCode);
      const couponPlan = couponInfo?.plans?.[0]?.code;
      cartState.checkout.couponPlanName = couponInfo?.plans?.[0]?.name;
      if (couponInfo.error
        || couponInfo.state !== 'redeemable'
        || couponInfo?.plans?.length > 1
        || state.value.coupon.inputtedCode.includes('reseller')
      ) {
        if (couponInfo.networkError) {
          state.value.coupon.invalidMessage = 'Could not validate coupon right now. Please try again.';
        } else if (couponInfo.state === 'maxed_out') {
          state.value.coupon.invalidMessage = 'Coupon has no more uses left';
        } else if (couponInfo.state === 'expired'){
          state.value.coupon.invalidMessage = 'Coupon has expired';
        } else {
          state.value.coupon.invalidMessage = 'Invalid coupon code';
        }
        state.value.coupon.invalid = true;
        state.value.coupon.success = false;
        if (couponIsVendorRouter.value) {
          state.value.coupon.routerCode = state.value.coupon.inputtedCode;
        }
        if (state.value.coupon) {
          state.value.coupon.inputtedCode = state.value.coupon.validatedCode;
        }
        return;
      }
      if (couponPlan && state.value.plan !== couponPlan) {
        state.value.coupon.invalid = true;
        cartState.checkout.couponPlanPath = `/?plan=${couponPlan}&coupon=${state.value.coupon.inputtedCode}`;
        cartState.checkout.showPlanChangeDialog = true;
      }
    });
  };

  const getPlanDescription = async () => {
    try {
      const response = await checkPlan(state.value.plan);
      if (response.status === 404) {
        router.replace({ query: { plan: 'speedify-unlimited-monthly' } });
      }
      if (!response.error) {
        planDescription.value = await response.json();
      }
    } catch (err) {
      Sentry.captureException(err); // maybe a network issue, let's watch
    }
  };

  // removeServersFromCheckout intentionally sets servers to 0; this flag tells the
  // servers watcher to allow that instead of flooring it back up to 1.
  let removingServers = false;

  const removeServersFromCheckout = () => {
    resetPaymentErrors();
    removingServers = true;
    state.value.servers = 0;
    state.value.serverCode = '';
    removeUrlQuery('servers');
    removeUrlQuery('serverCode');
  };

  // Bumped each time a quantity input is clamped down to quantityLimit, so the UI can
  // flash a "max reached" popover. A counter (rather than a boolean) lets repeat
  // over-limit attempts re-trigger the notice even though the clamped value is unchanged.
  const quantityLimitBump = ref(0);
  const serversLimitBump = ref(0);

  // We set up watchers here since html min/max attributes are not capable of handling
  // manual user changes to the input field. If a user manually changes it to a 0
  // or a negative number, set it back to 1
  // or squash floats down to nearest integer (3.5 -> 3)
  watch(
    () => state.value.quantity,
    newSubscriptionQuantity => {
      if (newSubscriptionQuantity <= 0) {
        state.value.quantity = 1;
      } else if (newSubscriptionQuantity > quantityLimit) {
        state.value.quantity = quantityLimit;
        quantityLimitBump.value++;
      } else if (!Number.isInteger(newSubscriptionQuantity)) {
        state.value.quantity = Math.floor(newSubscriptionQuantity);
      }
      // workaround: https://github.com/Connectify/speedify-cart/pull/340#discussion_r1525411310
      updateUrlQuery('quantity', state.value.quantity);
      resetPaymentErrors();
    },
  );

  watch(
    () => state.value.servers,
    newServerQuantity => {
      // Deliberate removal via the Remove button: allow 0, don't floor to 1.
      if (removingServers) {
        removingServers = false;
        removeUrlQuery('servers');
        return;
      }
      if (newServerQuantity <= 0) {
        state.value.servers = 1;
      } else if (newServerQuantity > quantityLimit) {
        state.value.servers = quantityLimit;
        serversLimitBump.value++;
      } else if (!Number.isInteger(newServerQuantity)) {
        state.value.servers = Math.floor(newServerQuantity);
      }
      // workaround: https://github.com/Connectify/speedify-cart/pull/340#discussion_r1525411310
      updateUrlQuery('servers', state.value.servers);
      resetPaymentErrors();
    },
  );


  watch(() => geoIPStore.details.zipCode, () => {
    // for now, will only run when geoIPStore.refresh is invoked
    state.value.postalCode = geoIPStore.details.zipCode;
  });

  // guard server purchases against free users on page load if logged in or at purchase time
  watch(() => state.value.isPaid, newIsPaid => {
    if (newIsPaid === false && !state.value.hasRouterSubs && state.value.plan.match(/server/i)) {
      const term = state.value.plan.includes('monthly') ? 'monthly' : 'yearly';
      let serverCode = null;
      if (state.value.plan.includes('selfhosted')) {
        if (state.value.plan.includes('personal')) {
          serverCode = `speedify-personal-selfhosted-server-${term}`;
        } else {
          serverCode = `speedify-selfhosted-server-${term}`;
        }
      }
      router.replace({
        query: {
          plan: `speedify-teams-unlimited-${term}`,
          servers: 1,
          coupon: state.value.coupon.validatedCode,
          addedTeam: 1,
          ...(serverCode && { serverCode })
        },
      });
    }
  });

  const update = (obj) => {
    for (const key in obj) {
      state.value[key] = obj[key];
    }
  };

  const ignoreUsersPaidStatus = computed(() => Boolean(state.value.plan.match(/router|server/i)));

  // grab info from cookie about the user. move this to a new store when we're interested in more than just email
  (async () => {
    const response = await verifyTokenUser();
    if (response.ok) {
      const tokenPayload = await verifyToken();
      if (tokenPayload?.userid) {
        state.value.loggedIn = true;
        state.value.email = tokenPayload.email;
        state.value.emailRepeat = tokenPayload.email;
        const subscriptionData = await getUserSubscriptions();
        if (!subscriptionData?.ownedSubscriptions) return;
        const { ownedSubscriptions } = subscriptionData;
        const activeOwnedSubscriptions = ownedSubscriptions.filter(sub => sub.active);
        // you should be barred from buying other unlimited products while you own one: isPaid
        // but not while you are a router only account
        // further, accounts with router subs *shouldn't* be pushed to buy a team when they want a server
        state.value.hasRouterSubs = activeOwnedSubscriptions.some(sub => sub.productName.match(/router/));
        state.value.isPaid = activeOwnedSubscriptions.some((sub) => {
          return !sub.productName.match(/router|server|free|trial|hotspot/);
        });
      }
    }
  })();

  // computed values

  // used to check coupon codes from route/url query
  const couponIsVendorRouter = computed(() => {
    return routerCouponPrefixes.split(',').some((prefix) => state.value.coupon.inputtedCode.startsWith(prefix));
  });

  const subscriptionProductRequiresCoupon = computed(() => {
    const list = [
      'speedify-unlimited-monthly-cardless-6m-trial',
      'speedify-vtx-unlimited-12m-cardless',
      'speedify-unlimited-monthly-cardless',
    ];
    return list.some((e) => state.value.plan == e);
  });

  const subscriptionProductEligibleForQuantityChange = computed(() => {
    const list = ['team', 'router', 'reseller', 'server'];
    return list.some((e) => state.value.plan.match(e));
  });

  const serversAvailable = computed(() => state.value.servers && (state.value.plan.includes('team') || enableIndividualServers));

  const exitOfferRelevant = computed(() => {
    // teams and routers are completely distinct categories of products from an individual nonrenewing pass
    // so don't make the customer think they're getting a deal on those
    const list = ['team', 'router'];
    return !list.some((e) => state.value.plan.match(e));
  });

  const freeTrialActive = computed(() => {
    return state.value.planTrial || state.value.couponTrial;
  });

  // Clears every trace of a previous purchase attempt. Called at the start of a new
  // attempt and whenever the cart changes (plan redirect, server removal, quantity or
  // coupon edits), since an error about the old checkout no longer applies.
  const resetPaymentErrors = () => {
    paymentState.value.purchaseFailed = false;
    paymentState.value.purchasePartiallySucceeded = false;
    paymentState.value.failureError = '';
    for (const panel of Object.values(panelState.value)) {
      panel.methodError = '';
      panel.partialSuccess = false;
    }
  };

  const setPaymentProcessing = (processing) => {
    paymentState.value.processing = processing;
  };

  const processPaymentToken = async (token, paymentMethod, analyticsDetails, captcha) => {
    resetPaymentErrors();
    paymentState.value.processing = true;
    state.value.email = typeof state.value.email === 'string' ? state.value.email.trim() : '';
    state.value.emailRepeat = typeof state.value.emailRepeat === 'string' ? state.value.emailRepeat.trim() : '';

    if (!state.value.email) {
      // Flip the spinner off, then let it unmount in its own flush before mounting the
      // error alert. Toggling both in one flush tripped a Vue block-patcher crash
      // ("Cannot read properties of null (reading 'insertBefore')").
      paymentState.value.processing = false;
      await nextTick();
      paymentState.value.purchaseFailed = true;
      paymentState.value.failureError = 'Email is required';
      return { error: { message: 'Email is required' } };
    }

    const result = await createPurchase(
      state.value,
      captcha || undefined,
      token?.id,
      analyticsDetails,
      paymentMethod
    );

    if (result.status === 'success') {
      orderCompleteRedirect(state.value.loggedIn);
    } else if (result.status === 'partial-success') {
      // Spinner off first, then flush, then mount the alert (see note above).
      paymentState.value.processing = false;
      await nextTick();
      paymentState.value.purchasePartiallySucceeded = true;
      paymentState.value.failureError = result.messages;
      return { partialSuccess: true, ...result };
    } else if (result.error?.errorCode === 'ERROR_MISSING_SPEEDIFY_LICENSE') {
      state.value.isPaid = false;
      paymentState.value.processing = false;
    } else {
      // Spinner off first, then flush, then mount the alert (see note above).
      paymentState.value.processing = false;
      await nextTick();
      paymentState.value.purchaseFailed = true;
      paymentState.value.failureError = result?.error?.message;
    }
    return result;
  };

  // Wrapper used by the wallet panels (PayPal/Venmo/Apple Pay/Google Pay): runs the
  // purchase and records any failure under the method's panelState slice. The panel
  // still owns emitting paymentProcessing/paymentFailed.
  // `method` is the camelCase panelState key; the backend expects snake_case
  // paymentType values (alongside 'credit_card'), so map before sending.
  const panelPaymentTypes = {
    paypal: 'paypal',
    venmo: 'venmo',
    applePay: 'apple_pay',
    googlePay: 'google_pay',
  };
  const handlePanelToken = async (method, token) => {
    const result = await processPaymentToken(token, panelPaymentTypes[method], analyticsStore.details);
    if (result.status !== 'success') {
      panelState.value[method].methodError = result.partialSuccess
        ? result.messages
        : (result.error?.message || 'Unable to process your payment. Please try again.');
      panelState.value[method].partialSuccess = result.partialSuccess || false;
    }
    return result;
  };

  return {
    checkoutData: state,
    quantityLimit,
    quantityLimitBump,
    serversLimitBump,
    getPlanDescription,
    planDescription,
    update,
    removeServersFromCheckout,
    checkoutPricing,
    initializeCheckoutPricing,
    ignoreUsersPaidStatus,
    couponIsVendorRouter,
    subscriptionProductRequiresCoupon,
    subscriptionProductEligibleForQuantityChange,
    serversAvailable,
    freeTrialActive,
    paymentState,
    panelState,
    resetPaymentErrors,
    setPaymentProcessing,
    processPaymentToken,
    handlePanelToken,
  };
});
